import { ArrowBack } from "@mui/icons-material";
import { Box, Grid, styled, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { OrganizationLogo } from "components/OrganizationLogo";
import { BottomLeftCloud } from "components/svgBackground/BottomLeftCloud";
import { GameTips } from "components/tips/GameTips";
import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { isDesktop, isMobile } from "react-device-detect";
import { useLocation, useNavigate } from "react-router-dom";
import { convertHexToRGBA } from "utils/commonUtils";
import { BackButton } from "./styles";

const LoginScreenContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	height: "100%",
	width: "100%",
	background: theme.organization.backgroundURI
		? `no-repeat center center fixed url(${theme.organization.backgroundURI})`
		: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
	backgroundSize: "cover",
	position: "relative",
	overflow: "auto",
	[theme.breakpoints.down("mobile")]: {
		background: convertHexToRGBA(theme.palette.primary.main, 0.52),
		overflow: "hidden",
	},
}));

const AuthLayout: FC<{ tips?: string[] }> = ({ children, tips }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const [isUnableBackButton, setIsUnableBackButton] = useState(false);
	const loginCardRef = useRef<HTMLDivElement>(null);
	const [backgroundImgHeight, setBackgroundImageHeight] = useState<number>(0);
	const classes = useStyles({ height: backgroundImgHeight });
	useLayoutEffect(() => {
		if (loginCardRef.current) {
			setBackgroundImageHeight(window.innerHeight - loginCardRef.current?.offsetHeight + 12);
		}
	}, [loginCardRef.current?.offsetHeight]);

	useEffect(() => {
		if (location.pathname.includes("reset-password")) setIsUnableBackButton(true);
		else setIsUnableBackButton(false);
	}, [location.pathname]);

	return (
		<LoginScreenContainer>
			{isMobile && (
				<div className={classes.layer}>
					<div className={classes.mirror}>
						<OrganizationLogo size={{ margin: "5px 0 0 0 ", width: "80%", height: "90%" }} />
					</div>
				</div>
			)}
			{isMobile && isUnableBackButton && (
				<BackButton variant="contained" onClick={() => navigate(-1)}>
					<ArrowBack />
				</BackButton>
			)}
			<div className={classes.container} ref={loginCardRef}>
				{isDesktop ? (
					<>
						<Grid item md={7} lg={6}></Grid>
						<Grid item md={5} lg={6} className={classes.resizeContainer}>
							{children}
						</Grid>
					</>
				) : (
					<>{children}</>
				)}
			</div>
			{isDesktop && (
				<Box position="fixed" bottom="9vh" left="5vw" maxWidth="30vw" zIndex={2}>
					{tips && tips.length > 0 && <GameTips tipsTexts={tips} />}
				</Box>
			)}
			{isDesktop && <BottomLeftCloud zIndex={1} opacity={0.8} color={theme.palette.primary.main} />}
		</LoginScreenContainer>
	);
};

export default AuthLayout;

const useStyles = makeStyles((theme) => ({
	container: {
		position: "fixed",
		display: "flex",
		width: "100%",
		zIndex: 2,
		alignItems: "center",
		[theme.breakpoints.up("desktop")]: {
			height: "100%",
			justifyContent: "flex-end",
		},
		[theme.breakpoints.down("mobile")]: {
			position: "absolute",
			bottom: 0,
			left: 0,
			overflowY: "scroll",
			paddingTop: "16px",
			backgroundColor: "white",
			borderTopLeftRadius: "15px",
			borderTopRightRadius: "15px",
		},
	},
	resizeContainer: {
		[theme.breakpoints.up("desktop")]: {
			display: "flex",
			justifyContent: "space-around",
			transform: "scale(80%)",
		},
	},
	layer: {
		[theme.breakpoints.down("mobile")]: {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			background: `no-repeat linear-gradient(${convertHexToRGBA(
				theme.palette.primary.main,
				0.52,
			)}, ${convertHexToRGBA(theme.palette.primary.main, 0.52)}), center center url(${
				theme.organization.backgroundURI
			})`,
			backgroundSize: "cover",
			backgroundColor: convertHexToRGBA(theme.palette.primary.main, 0.52),
			height: ({ height }: any) => height,
		},
	},
	mirror: {
		display: "flex",
		justifyContent: "center",
		width: "80%",
		marginBottom: 12,
		background: `linear-gradient(to bottom right, ${convertHexToRGBA("#FFFFFF", 0.42)},  ${convertHexToRGBA(
			"#FFFFFF",
			0.06,
		)}) `,
		backdropFilter: "blur(6px)",
		WebkitBackdropFilter: "blur(6px)",
		borderRadius: 10,
		height: "80%",
		maxHeight: 120,
	},
}));
